/*
  barna flow — S2 cart page behaviour (sections/s2-cart.liquid).

  The theme's cart.js re-renders only the old cart-template's fixed #MinimogCart ids, so the S2
  page brings this small controller instead:
  - quantity steppers (<m-quantity-input> from theme-global.js fires change) and remove buttons
    post /cart/change.js, one request at a time, in order
  - the section asks for its own HTML back through the Section Rendering API and swaps each
    [data-s2cp-region], leaving the Selleasy app block untouched
  - the theme's cartUpdate event is emitted, so the header count stays right, and received, so
    anything else that changes the cart re-renders the page
  - the mobile checkout bar hides while the summary's checkout button is on screen
  - on desktop a summary column taller than the screen may scroll to its end before it sticks
*/

(() => {
  if (customElements.get('s2-cart-page')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  class S2CartPage extends HTMLElement {
    connectedCallback() {
      this.queue = Promise.resolve();
      this.timers = new Map();

      this.onGroupedSections = (event) => event.detail.sections.push(this.dataset.sectionId);
      this.onChange = this.handleChange.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onRefresh = () => this.enqueue(() => this.refresh());

      document.addEventListener('cart:grouped-sections', this.onGroupedSections);
      document.addEventListener('cart:refresh', this.onRefresh);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);

      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        this.unsubscribe = window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, (event) => {
          if (event && event.cart && !event.cart.errors) this.render(event.cart);
        });
      }

      this.watchBar();
      this.watchAside();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:grouped-sections', this.onGroupedSections);
      document.removeEventListener('cart:refresh', this.onRefresh);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      if (this.unsubscribe) this.unsubscribe();
      if (this.observer) this.observer.disconnect();
      if (this.asideObserver) this.asideObserver.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    handleChange(event) {
      const input = event.target.closest('.m-quantity__input');
      if (!input || !this.contains(input)) return;
      const line = input.dataset.index;
      // Rapid taps on + collapse into one request carrying the final number.
      window.clearTimeout(this.timers.get(line));
      this.timers.set(
        line,
        window.setTimeout(() => {
          this.timers.delete(line);
          const quantity = Math.max(0, parseInt(input.value, 10) || 0);
          this.enqueue(() => this.update(line, quantity));
        }, 300)
      );
    }

    handleClick(event) {
      const button = event.target.closest('[data-s2cp-remove]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.enqueue(() => this.update(button.dataset.index, 0));
    }

    enqueue(task) {
      this.queue = this.queue.then(task).catch((error) => console.error('[s2-cart-page]', error));
      return this.queue;
    }

    bundledSections() {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );
      return [...new Set(sections)];
    }

    async update(line, quantity) {
      this.setBusy(true);
      this.showError('');
      try {
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(line),
            quantity,
            sections: this.bundledSections(),
            sections_url: window.location.pathname,
          }),
        });
        const cart = await response.json();
        if (!response.ok || cart.status) {
          this.showError(cart.description || cart.message || 'Something went wrong');
          await this.refresh();
          return;
        }
        this.publish(cart);
      } finally {
        this.setBusy(false);
      }
    }

    // Same event the theme's own cart and product forms emit. The cart has already changed by
    // now, so a subscriber that throws (a theme or app script) is logged, not shown as a failure.
    publish(cart) {
      let rendered = false;
      try {
        if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart });
          rendered = Boolean(this.unsubscribe);
        }
      } catch (error) {
        console.error('[s2-cart-page] cart update subscriber failed', error);
      }
      if (!rendered) this.render(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }

    render(cart) {
      const html = cart.sections && cart.sections[this.dataset.sectionId];
      if (html) {
        this.swap(html);
      } else {
        this.enqueue(() => this.refresh());
      }
    }

    async refresh() {
      const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(this.dataset.sectionId)}`);
      if (response.ok) this.swap(await response.text());
    }

    swap(html) {
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('s2-cart-page');
      if (!next) return;

      // Keep keyboard users on the control they just used.
      const active = document.activeElement;
      const activeLine = active && this.contains(active) ? active.closest('[data-cart-item]') : null;
      const focusTarget = activeLine
        ? { id: activeLine.id, name: active.getAttribute('name'), remove: active.hasAttribute('data-s2cp-remove') }
        : null;

      if (next.dataset.empty !== this.dataset.empty) {
        // Emptied, or filled again from elsewhere: the whole layout changes.
        this.dataset.empty = next.dataset.empty;
        this.innerHTML = next.innerHTML;
        this.watchBar();
        this.watchAside();
        return;
      }

      this.querySelectorAll('[data-s2cp-region]').forEach((region) => {
        const fresh = next.querySelector(`[data-s2cp-region="${region.dataset.s2cpRegion}"]`);
        if (fresh) region.innerHTML = fresh.innerHTML;
      });

      if (focusTarget) {
        const line = document.getElementById(focusTarget.id);
        let target = null;
        if (line && focusTarget.remove) target = line.querySelector('[data-s2cp-remove]');
        else if (line && focusTarget.name) target = line.querySelector(`[name="${focusTarget.name}"]`);
        (target || this.querySelector('[data-s2cp-region="lines"]'))?.focus?.({ preventScroll: true });
      }
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      this.querySelector('[data-s2cp-region="lines"]')?.setAttribute('aria-busy', String(busy));
    }

    showError(message) {
      const error = this.querySelector('[data-s2cp-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    watchBar() {
      if (this.observer) this.observer.disconnect();
      const bar = this.querySelector('[data-s2cp-bar]');
      const checkout = this.querySelector('[data-s2cp-checkout]');
      if (!bar || !checkout || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-hidden', entry.isIntersecting);
      });
      this.observer.observe(checkout);
    }
  }

  S2CartPage.prototype.watchAside = function watchAside() {
    // CSS sticky can't see the column's own height: with a fixed top, a summary column taller
    // than the screen would keep its reviews and payment icons out of reach. The offset turns
    // negative by the overflow, so the column scrolls to its end first and sticks there.
    if (this.asideObserver) this.asideObserver.disconnect();
    const aside = this.querySelector('.s2cp__aside');
    if (!aside || !('ResizeObserver' in window)) return;
    const update = () => {
      const top = Math.min(96, window.innerHeight - aside.offsetHeight - 24);
      this.style.setProperty('--s2cp-aside-top', `${top}px`);
    };
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    this.onResize = update;
    window.addEventListener('resize', this.onResize, { passive: true });
    this.asideObserver = new ResizeObserver(update);
    this.asideObserver.observe(aside);
    update();
  };

  customElements.define('s2-cart-page', S2CartPage);
})();
